/**
 * Data-access hooks for conversations — the ONLY place components touch
 * conversation server-state. Components never call useQuery/useMutation against
 * the API directly (andavo-dev convention); they call these hooks, which wrap
 * the query/mutation factories.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  conversationMutations,
  conversationQueries,
} from './conversations-query';

export function useConversations() {
  return useQuery(conversationQueries.list());
}

export function useConversation(id: string) {
  return useQuery(conversationQueries.detail(id));
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    ...conversationMutations.create(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: conversationQueries.all(),
      });
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    ...conversationMutations.update(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: conversationQueries.all(),
      });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    ...conversationMutations.remove(),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: conversationQueries.all(),
      });
    },
  });
}

/**
 * Submit thumbs-up/down feedback. No cache invalidation — feedback does not
 * mutate any conversation the UI reads back (it files a learning issue).
 */
export function useSubmitFeedback() {
  return useMutation(conversationMutations.feedback());
}
